import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { InputField, TextareaField } from "../../components/ui/Inputs";
import SelectOptions from "../../components/ui/SelectOptions";
import { DashboardOutlinedButton, PrimaryButton } from "../../components/ui/Button";
import {
  useLodgeComplaint,
  isPendingInformation,
  MAX_TOTAL_SIZE_MB,
} from "./hooks/useLodgeComplaint";
import { ComplaintPath, ComplaintSuccessData, FileError, NoTicketSubPath } from "./complaint.types";
import { REOPEN_REASONS } from "./support.types";
import { useQrcIdentifiers } from "./qrcIdentifiers";
import { qrcLanOptionsSelector } from "../../services/redux/slices/authSlice";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import Card from "../../components/ui/Card";
import TicketReflectionNotice from "../../components/common/TicketReflectionNotice";
import SupportCategoriesGrid from "../../components/common/SupportCategoriesGrid";

// ─── Constants ────────────────────────────────────────────────────────────────

const DESC_MAX = 500;
const DESC_MIN = 20;
const ACCEPTED_EXT = ".pdf,.jpg,.jpeg,.png";

// From the shared list so the thread can bold these when they lead a ticket description
// (see ALL_TICKET_REASONS in support.types).
const COMPLAINT_REASONS = REOPEN_REASONS.map((reason) => ({ value: reason, label: reason }));

const SUBPATH_OPTIONS: { value: NoTicketSubPath; label: string }[] = [
  { value: "query",     label: "Raise a Query / Request" },
  { value: "complaint", label: "Lodge a Complaint" },
];

// ─── Checkbox icon ────────────────────────────────────────────────────────────

const LcCheckbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span className={`lc-checkbox${checked ? " lc-checkbox--checked" : ""}`}>
    {checked && <CheckIcon className="lc-checkbox__icon" />}
  </span>
);

// ─── File Upload Zone ─────────────────────────────────────────────────────────

interface FileUploadProps {
  attachments: File[];
  fileErrors: FileError[];
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  addFiles: (f: FileList | null) => void;
  removeFile: (i: number) => void;
}

const FileUploadZone: React.FC<FileUploadProps> = ({
  attachments, fileErrors, dragOver, setDragOver, addFiles, removeFile,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <div
        className={`lc-dropzone${dragOver ? " lc-dropzone--drag-over" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
      >
        <FileUploadOutlinedIcon className="lc-dropzone__icon" />
        <p className="lc-dropzone__text">Drop File here or click to upload</p>
        <p className="lc-dropzone__hint">
          Max size: {MAX_TOTAL_SIZE_MB}MB. Supported: PDF, JPG, PNG.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXT}
          multiple
          hidden
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {fileErrors.map((fe, i) => (
        <Alert key={i} severity="warning" className="lc-file-alert" sx={{ mt: 1 }}>
          {fe.name ? `${fe.name}: ` : ""}{fe.reason}
        </Alert>
      ))}

      <Collapse in={attachments.length > 0}>
        <div className="lc-file-list">
          {attachments.map((file, i) => (
            <div key={i} className="lc-file-row">
              <span className="lc-file-row__name">
                {file.name}
                <span className="lc-file-row__size">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
              </span>
              <div className="lc-file-row__actions">
                <button
                  type="button"
                  className="lc-file-row__view"
                  onClick={() => {
                    const url = URL.createObjectURL(file);
                    window.open(url, "_blank", "noopener,noreferrer");
                    setTimeout(() => URL.revokeObjectURL(url), 10000);
                  }}
                  aria-label="View file"
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </button>
                <button type="button" className="lc-file-row__del" onClick={() => removeFile(i)} aria-label="Remove">
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

// ─── No-Ticket Form ───────────────────────────────────────────────────────────

interface NoTicketFormValues { loanAccountNumber: string; details: string; }

interface NoTicketFormProps extends FileUploadProps {
  noTicketSubPath: NoTicketSubPath | null;
  submitStatus: string;
  loanAccountOptions: { value: string; label: string; type: string }[];
  onSubmit: (loan: string, details: string) => Promise<void>;
}

const noTicketSchema = yup.object({
  loanAccountNumber: yup.string().required("Loan account / application number is required"),
  details: yup
    .string()
    // Trim before the length rules, otherwise whitespace counts towards the minimum and a
    // description of nothing but spaces submits. Also keeps padding out of what we send.
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .required("This field is required")
    .min(DESC_MIN, `Minimum ${DESC_MIN} characters required`)
    .max(DESC_MAX, `Maximum ${DESC_MAX} characters allowed`),
});

const NoTicketForm: React.FC<NoTicketFormProps> = ({
  noTicketSubPath, submitStatus, loanAccountOptions, onSubmit,
  attachments, fileErrors, dragOver, setDragOver, addFiles, removeFile,
}) => {
  const { control, handleSubmit, watch, formState: { errors, isValid } } =
    useForm<NoTicketFormValues>({
      resolver: yupResolver(noTicketSchema),
      mode: "onChange",
      defaultValues: { loanAccountNumber: "", details: "" },
    });

  // Trimmed, to match what the schema validates — otherwise a box of spaces reads "25 / 500"
  // while the field is still rejected for being under the minimum.
  const detailsLen = (watch("details") ?? "").trim().length;
  const isLoading = submitStatus === "loading";
  const isComplaint = noTicketSubPath === "complaint";

  const onFormSubmit: SubmitHandler<NoTicketFormValues> = (data) =>
    onSubmit(data.loanAccountNumber, data.details);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
      <div className="lc-form-field">
        <Controller
          name="loanAccountNumber"
          control={control}
          render={({ field }) => (
            <SelectOptions
              options={loanAccountOptions}
              label="Loan Account/Application Number"
              value={field.value}
              isMandatory
              onChange={(val) => field.onChange(String(val))}
              error={!!errors.loanAccountNumber}
              helperText={errors.loanAccountNumber?.message}
            />
          )}
        />
      </div>

      <div className="lc-form-field">
        <label className="lc-form-label">
          {isComplaint ? "Complaint Details" : "Description"}{" "}
          <span className="lc-form-label__req">*</span>
        </label>
        <Controller
          name="details"
          control={control}
          render={({ field }) => (
            <TextareaField
              {...field}
              fullWidth
              multiline
              rows={4}
              placeholder={
                isComplaint
                  ? "Please describe your complaint here..."
                  : "Please describe your request or query here..."
              }
              error={!!errors.details}
              inputProps={{ maxLength: DESC_MAX }}
              borderRadius="8px"
              padding="12px 14px"
            />
          )}
        />
        <div className="lc-char-row">
          <FormHelperText error={!!errors.details} sx={{ m: 0 }}>
            {errors.details?.message}
          </FormHelperText>
          <span className={`lc-char-count${detailsLen < DESC_MIN ? " lc-char-count--warn" : ""}`}>
            {detailsLen} / {DESC_MAX} Characters (Min {DESC_MIN} required)
          </span>
        </div>
      </div>

      <div className="lc-form-field">
        <label className="lc-form-label">Supporting Document</label>
        <FileUploadZone
          attachments={attachments} fileErrors={fileErrors}
          dragOver={dragOver} setDragOver={setDragOver}
          addFiles={addFiles} removeFile={removeFile}
        />
      </div>

      <div className="lc-form-submit">
        <PrimaryButton type="submit" disabled={!isValid || isLoading} className="lc-submit-btn">
          {isLoading
            ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Submitting…</>
            : "Submit"}
        </PrimaryButton>
      </div>
    </form>
  );
};

// ─── With-Ticket Body (verify + form) ────────────────────────────────────────

interface WithTicketFormValues { complaintReason: string; additionalContext: string; }

interface WithTicketBodyProps extends FileUploadProps {
  ticketIdInput: string;
  setTicketIdInput: (v: string) => void;
  verifiedTicketId: string;
  withTicketFormVisible: boolean;
  verifyStatus: string;
  verifyMessage: string | null;
  submitStatus: string;
  onVerify: () => Promise<void>;
  onSubmit: (reason: string, context: string) => Promise<void>;
}

const withTicketSchema = yup.object({
  complaintReason: yup.string().required("Please select a complaint reason"),
  additionalContext: yup
    .string()
    // See noTicketSchema — trimmed so spaces can't satisfy the minimum.
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .required("Additional context is required")
    .min(DESC_MIN, `Minimum ${DESC_MIN} characters required`)
    .max(DESC_MAX, `Maximum ${DESC_MAX} characters allowed`),
});

const WithTicketBody: React.FC<WithTicketBodyProps> = ({
  ticketIdInput, setTicketIdInput, verifiedTicketId, withTicketFormVisible,
  verifyStatus, verifyMessage, submitStatus, onVerify, onSubmit,
  attachments, fileErrors, dragOver, setDragOver, addFiles, removeFile,
}) => {
  const { control, handleSubmit, watch, formState: { errors, isValid } } =
    useForm<WithTicketFormValues>({
      resolver: yupResolver(withTicketSchema),
      mode: "onChange",
      defaultValues: { complaintReason: "", additionalContext: "" },
    });

  const contextLen = (watch("additionalContext") ?? "").trim().length;
  const isVerifying = verifyStatus === "loading";
  const isLoading = submitStatus === "loading";
  const isVerified = !!verifiedTicketId;

  const onFormSubmit: SubmitHandler<WithTicketFormValues> = (data) =>
    onSubmit(data.complaintReason, data.additionalContext);

  return (
    <div>
      {/* Ticket ID input + Verify button */}
      <div className="lc-form-field">
        <label className="lc-form-label">
          Enter your Ticket ID <span className="lc-form-label__req">*</span>
        </label>
        <div className="lc-verify-row">
          <InputField
            fullWidth
            placeholder="e.g. 8767564"
            value={ticketIdInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketIdInput(e.target.value)}
            error={verifyStatus === "not-found"}
            helperText={
              verifyStatus === "not-found"
                ? verifyMessage ?? ""
                : ""
            }
            borderRadius="8px"
            disabled={isVerified}
          />
          {!isVerified && (
            <PrimaryButton
              disabled={!ticketIdInput.trim() || isVerifying}
              onClick={onVerify}
              className="lc-verify-btn"
            >
              {isVerifying ? <CircularProgress size={16} color="inherit" /> : "Verify"}
            </PrimaryButton>
          )}
        </div>
      </div>

      {/* Complaint form — shown only after user clicks No on the verify dialog */}
      <Collapse in={withTicketFormVisible} unmountOnExit>
        <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <div className="lc-ticket-banner">
            Complaint against ticket: <strong>{verifiedTicketId}</strong>
          </div>

          <div className="lc-form-field">
            <Controller
              name="complaintReason"
              control={control}
              render={({ field }) => (
                <SelectOptions
                  options={COMPLAINT_REASONS}
                  label="Complaint Reason"
                  value={field.value}
                  isMandatory
                  onChange={(val) => field.onChange(String(val))}
                  error={!!errors.complaintReason}
                  helperText={errors.complaintReason?.message}
                />
              )}
            />
          </div>

          <div className="lc-form-field">
            <label className="lc-form-label">
              Additional Context <span className="lc-form-label__req">*</span>
            </label>
            <Controller
              name="additionalContext"
              control={control}
              render={({ field }) => (
                <TextareaField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Describe the issue in detail…"
                  error={!!errors.additionalContext}
                  inputProps={{ maxLength: DESC_MAX }}
                  borderRadius="8px"
                  padding="12px 14px"
                />
              )}
            />
            <div className="lc-char-row">
              <FormHelperText error={!!errors.additionalContext} sx={{ m: 0 }}>
                {errors.additionalContext?.message}
              </FormHelperText>
              <span className={`lc-char-count${contextLen < DESC_MIN ? " lc-char-count--warn" : ""}`}>
                {contextLen} / {DESC_MAX} Characters (Min {DESC_MIN} required)
              </span>
            </div>
          </div>

          <div className="lc-form-field">
            <label className="lc-form-label">Supporting Document</label>
            <FileUploadZone
              attachments={attachments} fileErrors={fileErrors}
              dragOver={dragOver} setDragOver={setDragOver}
              addFiles={addFiles} removeFile={removeFile}
            />
          </div>

          <div className="lc-form-submit">
            <PrimaryButton type="submit" disabled={!isValid || isLoading} className="lc-submit-btn">
              {isLoading
                ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Submitting…</>
                : "Submit"}
            </PrimaryButton>
          </div>
        </form>
      </Collapse>
    </div>
  );
};

// ─── Verify Dialog ────────────────────────────────────────────────────────────

interface VerifyDialogProps {
  open: boolean;
  ticketId: string;
  // Ticket status from verify-ticket. "Pending Information" means we're waiting on the customer,
  // so the prompt asks for the missing details instead of asking for more time.
  ticketStatus: string;
  onYes: () => void;
  onNo: () => void;
}

const VerifyDialog: React.FC<VerifyDialogProps> = ({ open, ticketId, ticketStatus, onYes, onNo }) => {
  const pendingInformation = isPendingInformation(ticketStatus);

  return (
    <Dialog
      open={open}
      onClose={onNo}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: "lc-vd-paper" }}
    >
      <DialogContent className="lc-vd-content">
        <div className="lc-vd-icon">
          <CheckIcon sx={{ fontSize: 32, color: "#fff" }} />
        </div>
        <Typography variant="h6" className="lc-vd-title">
          {pendingInformation ? "We need a few details" : "We're working on it!"}
        </Typography>
        <Typography variant="body2" className="lc-vd-subtitle">
          {pendingInformation ? (
            <>
              We&apos;ve asked for some additional details on your ticket so we can resolve your
              query, request or complaint.
            </>
          ) : (
            <>
              Please allow us a few more days to resolve your request for Ticket
              <span className="lc-vd-ticket-id" style={{ marginLeft: "10px" }}>{ticketId}</span>
            </>
          )}
        </Typography>
        <Typography variant="body2" className="lc-vd-question">
          {pendingInformation
            ? "Would you like to share the details?"
            : "Would you like to give us more time to resolve this?"}
        </Typography>
        <div className="lc-vd-actions">
          <button className="lc-vd-btn" onClick={onYes}>Yes</button>
          <button className="lc-vd-btn" onClick={onNo}>No</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Success Dialog ───────────────────────────────────────────────────────────

const SuccessDialog: React.FC<{
  successData: ComplaintSuccessData | null;
  onViewTickets: () => void;
  onGoToDashboard: () => void;
  isAuthenticated: boolean;
  isComplaint: boolean;
}> = ({ successData, onViewTickets, onGoToDashboard, isAuthenticated, isComplaint }) => (
  <Dialog
    open={Boolean(successData)}
    onClose={onViewTickets}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, p: 3 } }}
    className="raise-request-modal"
  >
    <DialogContent sx={{ p: 0, textAlign: "center" }}>
      {/* Teal circle checkmark */}
      <Box
        sx={{
          width: 68,
          height: 68,
          borderRadius: "50%",
          bgcolor: "#00B3B5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2.5,
        }}
      >
        <CheckIcon sx={{ fontSize: 38, color: "#fff" }} />
      </Box>

      {/* Title */}
      <Typography variant="h6" className="raise-request-success-label" sx={{ mb: 1 }}>
        {isComplaint ? "Complaint Registered!" : "Request Successfully Submitted!"}
      </Typography>

      {/* Subtitle */}
      <Typography variant="body2" className="raise-request-sub-label" sx={{ mb: 3 }}>
        {isComplaint
          ? "We have logged your concern. Our team is reviewing the details, and we will get back to you "
          : "Your request has been submitted and is being processed."}
      </Typography>

      {/* Info table */}
      <Box sx={{ mb: 2.5 }}>
        <div className="view-tickets-container">
          <Box className="raise-request-info-row raise-request-info-row-b-1">
            <Typography className="inside-page-row-lable">Ticket Number</Typography>
            <Typography className="inside-page-row-value">{successData?.ticketId}</Typography>
          </Box>
          <Box
            className={
              successData?.previousTicketId
                ? "raise-request-info-row"
                : "raise-request-info-row raise-request-info-row-b-2"
            }
            sx={
              successData?.previousTicketId
                ? { border: "1px solid #E5E5E5", borderBottomWidth: 0, borderRadius: 0 }
                : undefined
            }
          >
            <Typography className="inside-page-row-lable">Date of Submission</Typography>
            <Typography className="inside-page-row-value">{successData?.submittedAt}</Typography>
          </Box>
          {/* Only the complaint-against-existing-ticket flow has a previous ticket. */}
          {successData?.previousTicketId && (
            <Box className="raise-request-info-row raise-request-info-row-b-2">
              <Typography className="inside-page-row-lable">Previous Ticket</Typography>
              <Typography className="inside-page-row-value">{successData.previousTicketId}</Typography>
            </Box>
          )}
        </div>
      </Box>

      <TicketReflectionNotice />

      {/* Note */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 0.5 }}>
        You can track the real-time status of this ticket anytime on your Portal dashboard.
      </Typography>

      {/* Action buttons */}
      <Box>
        <PrimaryButton onClick={onViewTickets} sx={{ mr: 1 }}>
          View Tickets
        </PrimaryButton>
        <DashboardOutlinedButton fullWidth onClick={onGoToDashboard}>
          {isAuthenticated ? "Go to Dashboard" : "Back to Help Center"}
        </DashboardOutlinedButton>
      </Box>
    </DialogContent>
  </Dialog>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const LodgeAComplaint: React.FC = () => {
  const hook = useLodgeComplaint();

  // Same rule as Raise a Request and View Tickets: list exactly what qrc/user-verification
  // returns.
    // qrc/user-verification returns both buckets — disbursed loans and in-flight applications
    // (see extractLanList) — so it is the whole list. user-application/fetch used to stand behind
    // it, but it only ever repeated the application half of the same data, and was requested on
    // every visit to fill a gap that no longer exists.
  const verifiedLanOptions = useQrcIdentifiers().map((lan) => ({
    value: lan,
    label: lan,
    type: "accountNumber",
  }));
  // A pre-login QRC visitor sees only what their own verification returned — never the
  // persisted option slice, which can still hold the loan accounts of whoever was logged in on
  // this tab earlier.
  const qrcLanOptions: string[] = useSelector(qrcLanOptionsSelector) ?? [];
  const mergedObject = !hook.isAuthenticated
    ? qrcLanOptions.map((lan) => ({ value: lan, label: lan, type: "accountNumber" }))
    : verifiedLanOptions;

  const breadcrumbItems = [
    { label: "Dashboard", path: "/" },
    { label: "Lodge Complaint" },
  ];

  return (
    <div className="lc-page">
      {/* <Breadcrumbs items={breadcrumbItems} /> */}
      <Grid container>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{ color: "#168794", fontSize: "15px", fontWeight: "600", marginBottom: "20px" }}
          >
            Lodge a Complaint
          </Typography>
        </Grid>
      </Grid>

      {/* Options accordion beside the support rail — flex-start and no railFillHeight so the rail
          sizes to its tiles rather than stretching alongside an expanded accordion. */}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={12}>
      {/* Options accordion card */}
      <Card padding="0px">

        {/* ── Option 1: With Ticket ── */}
        <div className="lc-accordion-item">
          <div
            className="lc-accordion-item__header"
            onClick={() => hook.handlePathSelect("with-ticket")}
            role="checkbox"
            aria-checked={hook.selectedPath === "with-ticket"}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && hook.handlePathSelect("with-ticket")}
          >
            <LcCheckbox checked={hook.selectedPath === "with-ticket"} />
            <span className="lc-accordion-item__label">
              I want to raise a complaint against an existing Ticket ID
            </span>
          </div>

          <Collapse in={hook.selectedPath === "with-ticket"} unmountOnExit>
            <div className="lc-accordion-item__body">
              <WithTicketBody
                ticketIdInput={hook.ticketIdInput}
                setTicketIdInput={hook.setTicketIdInput}
                verifiedTicketId={hook.verifiedTicketId}
                withTicketFormVisible={hook.withTicketFormVisible}
                verifyStatus={hook.verifyStatus}
                verifyMessage={hook.verifyMessage}
                submitStatus={hook.submitStatus}
                onVerify={hook.handleVerify}
                onSubmit={hook.handleWithTicketSubmit}
                attachments={hook.attachments}
                fileErrors={hook.fileErrors}
                dragOver={hook.dragOver}
                setDragOver={hook.setDragOver}
                addFiles={hook.addFiles}
                removeFile={hook.removeFile}
              />
            </div>
          </Collapse>
        </div>

        {/* divider */}
        <div className="lc-accordion-divider" />

        {/* ── Option 2: No Ticket ── */}
        <div className="lc-accordion-item">
          <div
            className="lc-accordion-item__header"
            onClick={() => hook.handlePathSelect("no-ticket")}
            role="checkbox"
            aria-checked={hook.selectedPath === "no-ticket"}
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && hook.handlePathSelect("no-ticket")}
          >
            <LcCheckbox checked={hook.selectedPath === "no-ticket"} />
            <span className="lc-accordion-item__label">I don&apos;t have a ticket ID</span>
          </div>

          <Collapse in={hook.selectedPath === "no-ticket"} unmountOnExit>
            <div className="lc-accordion-item__body">
              {/* Sub-path selection */}
              <p className="lc-subpath__heading">How would you like to proceed?</p>
              <div className="lc-subpath-group">
                {SUBPATH_OPTIONS.map(({ value, label }) => (
                  <div
                    key={value}
                    className={`lc-subpath-row${hook.noTicketSubPath === value ? " lc-subpath-row--selected" : ""}`}
                    onClick={() => hook.handleSubPathSelect(value)}
                    role="radio"
                    aria-checked={hook.noTicketSubPath === value}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && hook.handleSubPathSelect(value)}
                  >
                    <LcCheckbox checked={hook.noTicketSubPath === value} />
                    <span className="lc-subpath-row__label">{label}</span>
                  </div>
                ))}
              </div>

              {/* Form — expands after sub-path selected */}
              <Collapse in={hook.noTicketSubPath !== null} unmountOnExit>
                <div className="lc-subpath-form">
                  <NoTicketForm
                    key={hook.noTicketSubPath}
                    noTicketSubPath={hook.noTicketSubPath}
                    submitStatus={hook.submitStatus}
                    loanAccountOptions={mergedObject}
                    onSubmit={hook.handleNoTicketSubmit}
                    attachments={hook.attachments}
                    fileErrors={hook.fileErrors}
                    dragOver={hook.dragOver}
                    setDragOver={hook.setDragOver}
                    addFiles={hook.addFiles}
                    removeFile={hook.removeFile}
                  />
                </div>
              </Collapse>
            </div>
          </Collapse>
        </div>

      </Card>
        </Grid>
      </Grid>

      <VerifyDialog
        open={hook.verifyDialogOpen}
        ticketId={hook.verifiedTicketId}
        ticketStatus={hook.verifiedTicketStatus}
        onYes={hook.handleVerifyDialogYes}
        onNo={hook.handleVerifyDialogNo}
      />

      <SuccessDialog
        successData={hook.successData}
        onViewTickets={hook.handleSuccessClose}
        onGoToDashboard={hook.handleGoToDashboard}
        isAuthenticated={hook.isAuthenticated}
        isComplaint={hook.selectedPath === "with-ticket" || hook.noTicketSubPath === "complaint"}
      />
    </div>
  );
};

export default LodgeAComplaint;